import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Telescope } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import SectionHeading from "../components/site/SectionHeading";

const FOUNDER_PHOTO = "/images/about/founder.jpeg";
const CALGARY_PHOTO = "/images/about/calgary.webp";
// Three of Sean's plates, the same ones as the Heart of House About page; captions in t.about.photos.
const PLATE_PHOTOS = [
  "/images/about/surf-and-earth.jpg",
  "/images/about/petits-fours-ika-2016.jpg",
  "/images/about/plums.jpg",
];

const About = () => {
  const { t } = useLanguage();
  const p = t.about;

  return (
    <div data-testid="page-about">
      <section className="border-b border-[#242424]">
        <div className="mise-container pt-6 md:pt-8 pb-14 md:pb-16 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <p className="mise-eyebrow">{p.eyebrow}</p>
            <h1 className="mt-5 text-5xl md:text-6xl font-semibold tracking-tight leading-[1.05]">
              {p.heading}{" "}
              <span className="mise-serif mise-accent">{p.headingItalic}</span>
            </h1>
            {p.subHeading && (
              <p
                className="mt-6 text-xl md:text-2xl italic font-semibold text-white leading-snug max-w-xl"
                data-testid="about-subheading"
              >
                {p.subHeading}
              </p>
            )}
            <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">{p.intro}</p>
            {p.pullQuote && (
              <p
                className="mt-4 mise-serif text-2xl md:text-3xl mise-accent leading-snug max-w-xl"
                data-testid="about-pullquote"
              >
                “{p.pullQuote}”
              </p>
            )}
          </div>
          <div className="lg:col-span-5">
            <div
              className="relative aspect-[4/5] w-full rounded-2xl overflow-hidden border border-[#444444] bg-[#0d0d0d]"
              data-testid="about-founder-visual"
            >
              <img
                src={FOUNDER_PHOTO}
                alt="Sean Burton, Founder & CEO of Mise"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent" aria-hidden />
              <div className="absolute bottom-5 left-5 right-5">
                <p className="mise-eyebrow !text-white/60">Sean Burton</p>
                <p className="mt-1 text-white text-lg font-medium">Founder &amp; CEO</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STORY: three of Sean's plates on the left, the founder's story on the right */}
      <section className="mise-section">
        <div className="mise-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 order-2 lg:order-1">
            {/* on desktop the plates spread evenly down the full height of the story */}
            <div className="grid gap-5 sm:grid-cols-3 lg:flex lg:h-full lg:flex-col lg:justify-between" data-testid="about-plates">
              {PLATE_PHOTOS.map((src, i) => (
                <figure key={src} className="m-0">
                  <div className="overflow-hidden rounded-2xl border border-[#444444] bg-[#0d0d0d]">
                    <img src={src} alt={p.photos[i].alt} loading="lazy" className="block w-full h-auto" />
                  </div>
                  <figcaption className="mt-2 text-[13px] text-white/55">{p.photos[i].caption}</figcaption>
                </figure>
              ))}
            </div>
          </div>
          <div className="lg:col-span-8 order-1 lg:order-2 space-y-6 text-lg text-white/80 leading-relaxed">
            <p className="mise-eyebrow text-center" data-testid="about-story-heading">{p.storyHeading}</p>
            {p.storyParas.map((para, i) => (
              <p key={i} data-testid={`about-story-para-${i}`}>{para}</p>
            ))}
            {p.signature && (
              <p
                className="mise-serif text-2xl mise-accent pt-4 border-t border-[#242424]"
                data-testid="about-signature"
              >
                {p.signature}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* MISSION / VISION: two glowing cards, the statement set large in the serif */}
      <section className="relative overflow-hidden mise-section border-t border-[#242424]">
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[900px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#E8490F]/10 blur-3xl"
          aria-hidden
        />
        <div className="mise-container relative grid md:grid-cols-2 gap-5">
          {[
            { n: "01", icon: Target, heading: p.missionHeading, text: p.missionText, testId: "about-mission", warm: true },
            { n: "02", icon: Telescope, heading: p.visionHeading, text: p.visionText, testId: "about-vision", warm: false },
          ].map(({ n, icon: Icon, heading, text, testId, warm }) => (
            <div
              key={testId}
              className="group rounded-[1.6rem] bg-gradient-to-br from-[#E8490F]/70 via-[#444444]/50 to-[#242424] p-px transition-shadow duration-300 hover:shadow-[0_30px_70px_-30px_rgba(232,73,15,0.55)]"
              data-testid={testId}
            >
              <div
                className={`relative h-full overflow-hidden rounded-[1.55rem] p-8 md:p-10 ${
                  warm ? "bg-gradient-to-br from-[#2b1810] via-[#1c1c1c] to-[#171717]" : "bg-gradient-to-bl from-[#231712] via-[#1a1a1a] to-[#151515]"
                }`}
              >
                {/* the word itself, huge and outlined, as a watermark */}
                <span
                  className="pointer-events-none absolute -bottom-6 -right-2 select-none text-[6.5rem] md:text-[8rem] font-bold uppercase leading-none tracking-tighter text-transparent [-webkit-text-stroke:1px_rgba(232,73,15,0.18)] transition-all duration-500 group-hover:[-webkit-text-stroke:1px_rgba(232,73,15,0.35)]"
                  aria-hidden
                >
                  {heading}
                </span>
                <div className="relative flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-[#E8490F]/40 bg-[#E8490F]/12 text-[#E8490F]">
                    <Icon size={20} />
                  </span>
                  <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#E8490F]">
                    {n} · {heading}
                  </p>
                </div>
                <span
                  className="relative mt-6 block h-[2px] w-12 bg-[#E8490F] transition-all duration-500 group-hover:w-24"
                  aria-hidden
                />
                <p className="relative mt-6 pb-10 mise-serif text-2xl md:text-[1.85rem] leading-[1.3] text-white [text-wrap:balance]">
                  {text}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY CALGARY */}
      <section className="mise-section border-t border-[#242424]">
        <div className="mise-container grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <SectionHeading title={p.whyCalgaryHeading} subtitle={p.whyCalgaryText} />
            <Link to="/contact" className="mt-8 mise-pill-primary inline-flex" data-testid="about-cta" data-track="about-contact">
              {t.nav.contact}
              <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-5">
            <div
              className="relative aspect-[4/3] w-full rounded-2xl overflow-hidden border border-[#444444] bg-[#0d0d0d]"
              data-testid="about-calgary-visual"
            >
              <img
                src={CALGARY_PHOTO}
                alt="Calgary skyline at dusk, Mise's home city"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/70 via-transparent to-transparent" aria-hidden />
              <div className="absolute bottom-5 left-5 right-5">
                <p className="mise-eyebrow !text-white/60">Headquarters</p>
                <p className="mt-1 text-white text-lg font-medium">Calgary, AB</p>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default About;
